import { motion } from "framer-motion";

function PricingPlans() {
  const plans = [
    {
      name: "Free",
      price: "0",
      monthlyPrice: "0",
      yearlyPrice: "0",
      description: "Great for getting started",
      features: [
        "Process up to 100 emails/month with AI",
        "Basic categorization (5 categories)",
        "Weekly email summary",
        "Basic bulk actions (archive, delete)",
        "Limited search capabilities",
        "Email community support"
      ],
      highlighted: false,
      buttonText: "Get Started Free",
      bestFor: "Personal Use"
    },
    {
      name: "Pro",
      price: "9.99",
      monthlyPrice: "9.99",
      yearlyPrice: "7.99",
      description: "Perfect for professionals",
      features: [
        "Unlimited emails processed with AI",
        "Advanced categorization (unlimited)",
        "Daily email summaries and insights",
        "Advanced bulk actions with custom rules",
        "Natural language search across all emails",
        "Priority customer support",
        "Scheduled automatic cleanups",
        "Email templates and response suggestions",
        "Attachment management and organization",
        "Analytics dashboard"
      ],
      highlighted: true,
      buttonText: "Start 14-Day Trial",
      bestFor: "Professionals"
    },
    {
      name: "Enterprise",
      price: "19.99",
      monthlyPrice: "19.99",
      yearlyPrice: "15.99",
      description: "For teams and businesses",
      features: [
        "All Pro features included",
        "Team collaboration features",
        "Custom domain support",
        "Admin dashboard and controls",
        "User management and permissions",
        "Advanced security features",
        "Dedicated API access",
        "Custom integrations",
        "Dedicated account manager",
        "SLA guaranteed uptime"
      ],
      highlighted: false,
      buttonText: "Contact Sales",
      bestFor: "Teams & Businesses"
    }
  ];

  return (
    <section className="py-12 px-4">
      <div className="container mx-auto max-w-7xl">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {plans.map((plan, index) => (
            <motion.div
              key={plan.name}
              className={`
                rounded-xl p-8 flex flex-col h-full relative
                ${
                  plan.highlighted
                    ? "bg-white border-2 border-[#ff8c42] shadow-[0_10px_50px_rgba(255,140,66,0.2)]"
                    : "bg-white border border-[#e2dad3] shadow-xl"
                }
              `}
              initial={{ opacity: 0, y: 50 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: index * 0.2 }}
              whileHover={{
                y: -15,
                boxShadow: plan.highlighted
                  ? "0 30px 60px rgba(255, 140, 66, 0.25)"
                  : "0 30px 60px rgba(0, 0, 0, 0.15)",
                transition: { duration: 0.3 },
              }}
            >
              {/* Best for badge */}
              <div className="bg-[#f9f6f4] px-3 py-1 rounded-full inline-block mb-4 self-start">
                <span className="text-xs font-medium text-[#694e42]">Best for {plan.bestFor}</span>
              </div>
              
              {/* Popular tag */}
              {plan.highlighted && (
                <div className="absolute top-4 right-4 bg-[#ff8c42] text-white text-xs font-bold py-1 px-3 rounded-full">
                  POPULAR
                </div>
              )}

              <h3 className="text-2xl font-bold text-[#694e42] mb-2">{plan.name}</h3>

              <div className="flex items-baseline mb-1">
                <span className="text-4xl font-bold text-[#694e42]">${plan.price}</span>
                <span className="text-[#8a7266] ml-2">/month per user</span>
              </div>
              
              <p className="text-[#8a7266] mb-6">{plan.description}</p>
              
              <motion.button
                className={`w-full py-3 rounded-lg font-medium mb-8 ${
                  plan.highlighted
                    ? "bg-[#ff8c42] hover:bg-[#ff7a29] text-white"
                    : "bg-[#f9f6f4] border border-[#694e42] text-[#694e42] hover:bg-[#f1ece8]"
                }`}
                whileHover={{ scale: 1.03 }}
                whileTap={{ scale: 0.97 }}
              >
                {plan.buttonText}
              </motion.button>

              <div className="space-y-4 flex-grow">
                <div className="text-sm font-medium text-[#694e42] uppercase tracking-wider mb-3">
                  {plan.highlighted ? "Everything you need:" : "Includes:"}
                </div>

                <ul className="space-y-4">
                  {plan.features.map((feature, i) => (
                    <li key={i} className="flex items-start">
                      <div
                        className={`mr-3 ${
                          plan.highlighted ? "text-[#ff8c42]" : "text-[#946e5d]"
                        } pt-0.5`}
                      >
                        <svg
                          className="w-5 h-5"
                          viewBox="0 0 24 24"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <path
                            d="M20 6L9 17L4 12"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          />
                        </svg>
                      </div>
                      <span className="text-[#694e42] text-sm">{feature}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Decorative pattern */}
              <div className="absolute bottom-0 left-0 right-0 h-12 overflow-hidden opacity-20">
                <svg
                  className="w-full h-full"
                  viewBox="0 0 100 40"
                  xmlns="http://www.w3.org/2000/svg"
                  preserveAspectRatio="none"
                >
                  <path
                    d="M0 40 C 20 0, 50 40, 100 0 L 100 40 L 0 40 Z"
                    fill={plan.highlighted ? "#ff8c42" : "#946e5d"}
                  />
                </svg>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}

export default PricingPlans;